import { useMemo, useState } from "react";
import { FiClock, FiInstagram, FiMail, FiMapPin } from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import { isEmpty, isValidEmail, isValidPhoneNumber } from "../utils/validators";
import { sendContactMessage } from "../services/contactService";

const Contact = () => {
  const initialForm = {
    fullName: "",
    email: "",
    phoneNumber: "",
    subject: "",
    message: "",
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactCards = useMemo(
    () => [
      {
        icon: <FiMail size={20} />,
        title: "Email",
        value: "hennanayra@gmail.com",
        note: "Best for booking questions, service details, and custom inquiries.",
      },
      {
        icon: <FiMapPin size={20} />,
        title: "Appointments",
        value: "Studio and mobile appointments available",
        note: "For mobile bookings, please include your address and any helpful arrival instructions.",
      },
      {
        icon: <FiClock size={20} />,
        title: "Scheduling",
        value: "By appointment",
        note: "Available times are based on the posted booking calendar and current availability.",
      },
    ],
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      form: "",
    }));

    setSubmitted(false);
    setSubmitMessage("");
  };

  const resetForm = () => {
    setForm(initialForm);
    setErrors({});
    setSubmitted(false);
    setSubmitMessage("");
  };

  const validate = () => {
    const nextErrors = {};

    if (isEmpty(form.fullName)) nextErrors.fullName = "Please enter your name.";
    if (!isValidEmail(form.email)) nextErrors.email = "Please enter a valid email address.";
    if (!isEmpty(form.phoneNumber) && !isValidPhoneNumber(form.phoneNumber)) {
      nextErrors.phoneNumber = "Please enter a valid phone number.";
    }
    if (isEmpty(form.subject)) nextErrors.subject = "Please add a subject.";
    if (isEmpty(form.message)) nextErrors.message = "Please enter your message.";

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      setSubmitted(false);
      setSubmitMessage("");
      setErrors({});

      const response = await sendContactMessage(form);

      setSubmitted(true);
      setSubmitMessage(
        response?.message ||
          "Your message has been sent successfully. You will receive a response as soon as possible."
      );
      setForm(initialForm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        form: error.message || "We could not send your message right now.",
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="Get In Touch"
          title="We would love to hear from you"
          subtitle="Reach out with booking questions, event details, service requests, or anything you would like to ask before booking."
          className="mb-8"
        />

        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
          <section className="content-card-lg p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-henna">Send a message</h2>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              Whether you are planning a personal appointment, a special event,
              or a larger gathering, you are welcome to share your questions
              here.
            </p>

            {submitted && (
              <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-4">
                <p className="text-sm font-semibold text-green-700">
                  Thank you for reaching out.
                </p>
                <p className="mt-1 text-sm text-green-700/90">{submitMessage}</p>
              </div>
            )}

            {errors.form && (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-700">
                  Message not sent
                </p>
                <p className="mt-1 text-sm text-red-700/90">{errors.form}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="form-grid two">
                <Input
                  label="Full Name"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  error={errors.fullName}
                  required
                  placeholder="Enter your full name"
                />

                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  error={errors.email}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-grid two">
                <Input
                  label="Phone Number"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  error={errors.phoneNumber}
                  placeholder="Optional"
                />

                <Input
                  label="Subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  error={errors.subject}
                  required
                  placeholder="What would you like help with?"
                />
              </div>

              <Input
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                error={errors.message}
                required
                textarea
                rows={6}
                placeholder="Share your event details, questions, preferred date, or anything helpful for your inquiry."
              />

              <div className="flex flex-wrap gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                >
                  Send Message
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={resetForm}
                  disabled={isSubmitting}
                >
                  Clear Form
                </Button>
              </div>
            </form>
          </section>

          <aside className="space-y-6">
            <div className="content-card-lg p-6">
              <h2 className="text-xl font-semibold text-henna">Contact details</h2>
              <div className="mt-5 space-y-4">
                {contactCards.map((card) => (
                  <div
                    key={card.title}
                    className="rounded-2xl border border-goldlight bg-white p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-soft-cream p-3 text-henna">
                        {card.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#3b2f2f]">
                          {card.title}
                        </p>
                        <p className="mt-1 text-sm font-medium text-henna">
                          {card.value}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-muted-warm">
                          {card.note}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="content-card-lg p-6">
              <h2 className="text-xl font-semibold text-henna">Helpful notes</h2>
              <div className="mt-4 space-y-3 text-sm leading-6 text-muted-warm">
                <p>
                  <span className="font-semibold text-[#3b2f2f]">Studio:</span>{" "}
                  you will go to the artist for your appointment.
                </p>
                <p>
                  <span className="font-semibold text-[#3b2f2f]">Mobile:</span>{" "}
                  the artist will come to your location.
                </p>
                <p>
                  For mobile appointments, please include the correct address and
                  any helpful details such as apartment number, gate code,
                  parking instructions, or landmarks.
                </p>
              </div>

              <div className="mt-5">
                <a
                  href="https://www.instagram.com/nayra.heena/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-goldlight px-4 py-3 text-sm font-medium text-[#3b2f2f] transition hover:bg-soft-cream hover:text-henna"
                >
                  <FiInstagram />
                  Follow on Instagram
                </a>
              </div>
            </div>
          </aside>
        </div>
      </PageContainer>
    </div>
  );
};

export default Contact;